import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, ExternalLink } from 'lucide-react';
import { projects, trainings, consultingServices } from '../data/mock';
import { useInView } from '../hooks/useInView';

const ProjectsPage = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.1 });
  const [projectsRef, projectsInView] = useInView({ threshold: 0.1 });
  const [trainingsRef, trainingsInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });

  const projectColors = [
    { gradient: 'from-emerald-400 to-green-600', shadow: 'shadow-emerald-500/30' },
    { gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/30' },
    { gradient: 'from-blue-400 to-indigo-600', shadow: 'shadow-blue-500/30' },
    { gradient: 'from-purple-400 to-violet-600', shadow: 'shadow-purple-500/30' },
    { gradient: 'from-rose-400 to-pink-600', shadow: 'shadow-rose-500/30' },
    { gradient: 'from-cyan-400 to-teal-600', shadow: 'shadow-cyan-500/30' },
  ];

  return (
    <>
      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] py-32 overflow-hidden" ref={headerRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-purple-400 to-violet-600 text-white text-sm font-bold rounded-full mb-6 shadow-lg shadow-purple-500/30">
              CASE STUDIES
            </span>
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Projects & <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-violet-300">Services</span>
            </h1>
            <p className="text-white/80 max-w-2xl text-xl leading-relaxed font-medium">
              Consultancy assignments, research projects, and capacity building initiatives.
            </p>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <section className="py-20 bg-white" ref={projectsRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${projectsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-emerald-400 to-green-600 text-white text-sm font-bold rounded-full mb-4 shadow-lg shadow-emerald-500/30">
              PORTFOLIO
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Research & <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-green-600">Consultancy</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => {
              const colorSet = projectColors[index % projectColors.length];
              return (
                <div
                  key={project.id}
                  className={`group bg-white rounded-3xl overflow-hidden border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${projectsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className={`bg-gradient-to-r ${colorSet.gradient} text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg ${colorSet.shadow}`}>
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-[#718096] mb-3">
                      <span className="flex items-center gap-1 font-medium">
                        <Calendar className="w-4 h-4" />
                        {project.year}
                      </span>
                      <span className="flex items-center gap-1 font-medium">
                        <User className="w-4 h-4" />
                        {project.role}
                      </span>
                    </div>
                    <h3 className="text-[#1C1C1C] font-black text-lg mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{project.title}</h3>
                    <p className="text-[#1F3C88] font-bold text-sm mb-3">{project.client}</p>
                    <p className="text-[#718096] text-sm leading-relaxed">{project.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trainings */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" ref={trainingsRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${trainingsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-bold rounded-full mb-4 shadow-lg shadow-amber-500/30">
              CAPACITY BUILDING
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Training <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">Programs</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {trainings.map((training, index) => {
              const colorSet = projectColors[index % projectColors.length];
              return (
                <div
                  key={training.id}
                  className={`group rounded-3xl overflow-hidden bg-gradient-to-br ${colorSet.gradient} shadow-xl ${colorSet.shadow} transition-all duration-500 hover:scale-105 hover:-translate-y-2 ${trainingsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={training.image}
                      alt={training.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-80"
                    />
                  </div>
                  <div className="p-6">
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold">
                      {training.year}
                    </span>
                    <h3 className="text-white font-black text-lg mt-3 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{training.title}</h3>
                    <p className="text-white/90 font-semibold text-sm mb-2">{training.institution}</p>
                    <p className="text-white/80 text-sm">
                      <span className="font-bold">Audience:</span> {training.audience}
                    </p>
                    <p className="text-white/80 text-sm mt-1">
                      <span className="font-bold">Focus:</span> {training.focus}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Consulting Services */}
      <section className="py-20 bg-white" ref={servicesRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-blue-400 to-indigo-600 text-white text-sm font-bold rounded-full mb-4 shadow-lg shadow-blue-500/30">
              SERVICES
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Consulting <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">Expertise</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consultingServices.map((service, index) => {
              const colorSet = projectColors[index % projectColors.length];
              return (
                <div
                  key={service.id}
                  className={`group rounded-3xl p-8 bg-gradient-to-br ${colorSet.gradient} shadow-xl ${colorSet.shadow} transition-all duration-500 hover:scale-105 hover:-translate-y-2 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <span className="text-2xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-white font-black text-xl mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>{service.title}</h3>
                  <p className="text-white/80 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#1F3C88] to-[#2A4FA8] text-white font-bold rounded-full shadow-xl shadow-[#1F3C88]/30 hover:scale-105 hover:-translate-y-1 transition-all duration-300">
              Request Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProjectsPage;